import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { Box, Typography, TextField, Button, MenuItem, CircularProgress } from '@mui/material';
import LegalLayout from './LegalLayout';

const SUBJECTS = [
  'שאלה לגבי המסלולים',
  'בקשת הצעת מחיר לחבילת חומרה',
  'תמיכה טכנית',
  'שאלה כללית',
  'אחר',
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Heebo, sans-serif',
    fontSize: '0.93rem',
    borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(30,58,95,0.2)' },
    '&:hover fieldset': { borderColor: '#1e3a5f' },
    '&.Mui-focused fieldset': { borderColor: '#9a7209' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Heebo, sans-serif',
    fontSize: '0.9rem',
    '&.Mui-focused': { color: '#9a7209' },
  },
};

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', synagogue: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          synagogue: form.synagogue || '—',
          subject: form.subject,
          message: form.message,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      );
      setSent(true);
    } catch (err) {
      console.error('EmailJS error:', err);
      alert('שגיאה בשליחה, נסו שוב או פנו ישירות ל-ineraigroup@gmail.com');
    } finally {
      setSending(false);
    }
  };

  return (
    <LegalLayout title="צור קשר" subtitle="נשמח לשמוע מכם — נחזור תוך יום עסקים">
      <Box sx={{ maxWidth: '560px', mx: 'auto' }}>

        {sent ? (
          <Box sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            bgcolor: 'white',
            borderRadius: 3,
            border: '1.5px solid rgba(30,58,95,0.1)',
          }}>
            <Typography sx={{
              fontFamily: 'Frank Ruhl Libre, serif',
              fontSize: '2rem',
              fontWeight: 900,
              color: '#1e3a5f',
              mb: 1.5,
            }}>
              תודה על פנייתכם
            </Typography>
            <Typography sx={{
              fontFamily: 'Heebo, sans-serif',
              fontSize: '1rem',
              color: '#5a6978',
              lineHeight: 1.8,
              mb: 3,
            }}>
              ההודעה נשלחה בהצלחה — נחזור אליכם בהקדם.
            </Typography>
            <Button
              onClick={() => setSent(false)}
              sx={{
                color: '#9a7209',
                fontFamily: 'Heebo, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(154,114,9,0.06)' },
              }}
            >
              שלח פנייה נוספת
            </Button>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              bgcolor: 'white',
              borderRadius: 3,
              border: '1.5px solid rgba(30,58,95,0.1)',
              p: { xs: 3, md: 4.5 },
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <TextField
                label="שם מלא"
                value={form.name}
                onChange={set('name')}
                required
                fullWidth
                sx={fieldSx}
              />
              <TextField
                label="כתובת מייל"
                type="email"
                value={form.email}
                onChange={set('email')}
                required
                fullWidth
                sx={fieldSx}
              />
            </Box>

            <TextField
              label="שם בית הכנסת / הארגון (אופציונלי)"
              value={form.synagogue}
              onChange={set('synagogue')}
              fullWidth
              sx={fieldSx}
            />

            <TextField
              select
              label="נושא הפנייה"
              value={form.subject}
              onChange={set('subject')}
              required
              fullWidth
              sx={fieldSx}
            >
              {SUBJECTS.map(s => (
                <MenuItem key={s} value={s} sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.9rem' }}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="הודעה"
              value={form.message}
              onChange={set('message')}
              required
              fullWidth
              multiline
              rows={5}
              sx={fieldSx}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={sending}
              sx={{
                bgcolor: '#9a7209',
                color: 'white',
                fontFamily: 'Heebo, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 6px 20px rgba(154,114,9,0.3)',
                '&:hover': {
                  bgcolor: '#b8860b',
                  boxShadow: '0 10px 28px rgba(154,114,9,0.4)',
                },
                transition: 'all 0.2s ease',
                '&.Mui-disabled': { bgcolor: 'rgba(154,114,9,0.4)', color: 'white' },
              }}
            >
              {sending ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'שלח הודעה'}
            </Button>

            <Typography sx={{
              fontFamily: 'Heebo, sans-serif',
              fontSize: '0.78rem',
              color: '#a0a9b4',
              textAlign: 'center',
              lineHeight: 1.6,
            }}>
              הפנייה תישלח ישירות אל ineraigroup@gmail.com
            </Typography>
          </Box>
        )}

        {/* Alternative contact */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography sx={{
            fontFamily: 'Heebo, sans-serif',
            fontSize: '0.85rem',
            color: '#8a94a0',
          }}>
            מעדיפים לפנות ישירות?{' '}
            <Box
              component="a"
              href="mailto:ineraigroup@gmail.com"
              sx={{ color: '#9a7209', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
            >
              ineraigroup@gmail.com
            </Box>
          </Typography>
        </Box>
      </Box>
    </LegalLayout>
  );
};

export default ContactPage;
